// Canonical pro↔city radius-matching engine (2026-07-29, user directive: "the engine
// isn't synced between cities and the radius they serve"). Before this file, FIVE
// different call sites (QuickConsultBar, waLinks, CategoryExpertsList, categoryData,
// ProsMap) each independently filtered `haversineKm(...) <= DEFAULT_RADIUS_KM` — a
// single hardcoded 40km everywhere, regardless of actual pro density around that city.
// That's exactly what breaks: 40km around Tel Aviv reaches dozens of pros; 40km around
// a peripheral town can reach ZERO even when a perfectly willing pro sits 45km away —
// the visitor got silently told "nobody serves you" while the same category had real
// supply just outside an arbitrary line. This is now the ONE place that decision gets
// made, isomorphic (no DB import) so both server routes and client components use it.
import { haversineKm, DEFAULT_RADIUS_KM, findCity } from './israelCities';
import { sameLocality, roundKm } from './geo';
import { isAwayUntilLater, type ProAvailInput } from './availability';
import { rankScore, sortByRankScore, type RankScoreInput } from './proRankScore';

// Expanding search ladder — try the tight/normal radius first (best match quality),
// only widen if that comes up short.
// 2026-08-23 (user directive, caught live on /טכנאי-מזגנים for נתניה — 70km pulled in
// Ashdod technicians, 63-65km away, when nothing closer than 25km was active): the old
// ladder's second rung (70km) was too permissive for a "reasonable local match" — a
// visitor picking their own city should never see a match that far away presented as
// "we found you N experts" without it reading as obviously too distant. Capped the
// ladder itself at 40km (user's explicit number). The "never show truly zero" fallback
// below (2026-08-19 directive, unchanged) still applies ONLY when the ENTIRE 40km ladder
// comes up empty — at that point every remaining pro in the category is returned,
// nearest-first, still flagged `expanded` and logged via logCityDemandGap so real supply
// gaps stay visible instead of silently either hiding the category or fudging the radius.
export const RADIUS_LADDER_KM = [DEFAULT_RADIUS_KM, 40] as const;
/** A rung of the ladder counts only once it holds this many pros (Founder 06/10/26, פנייה 13986 נתיבות): with 1, a single
 *  "country"-mode pro (Daniel, 54 km) satisfied the 25 km rung and the customer got ONE card while 76/103 km pros sat unused.
 *  The ONE number for every consumer (WhatsApp links, city page, x3d list, QuickConsultBar). */
export const MIN_MATCHES = 3;
/** How many cards a category / city page shows before "הצגת עוד מומחים" (Founder 2026-09-27). The homepage strip for a
 *  category (lib/homeExperts) shows EXACTLY this many of cityPageOrder().list — bughunter 30/09/26: home showed 5, the page 4. */
export const CITY_PAGE_INITIAL = 4;

// 2026-09-22 (Founder directive, ליאור דרור/אשדוד): a professional may carry a PER-PRO
// service-area override that beats the shared radius ladder — `serviceCities` (comma
// list of city names; when set he matches ONLY those visitor cities) and/or
// `serviceRadiusKm` (his own max distance). Both null = the default ladder. A pro with
// an override is NEVER shown outside his area — not even by the "everyone, distance-
// sorted" last-resort fallback. Columns: he_professionals.service_cities /
// service_radius_km (editable in the admin pro modal).
export type ServiceMode = 'default' | 'radius' | 'country' | 'city';
export type MatchTarget = {
  lat: number | null; lng: number | null;
  serviceCities?: string | null; serviceRadiusKm?: number | null;
  // Founder 2026-09-27: the pro chooses in his portal — default ladder / own radius / nationwide / only his own city
  serviceMode?: ServiceMode | null; service_mode?: ServiceMode | null; city?: string | null; proCity?: string | null;
};

const normCity = (s: string) => s.replace(/[\s\-'"״׳]/g, '').replace(/^ה/, '');
export function serviceCityList(p: MatchTarget): string[] {
  return (p.serviceCities || '').split(/[,;|]/).map(c => c.trim()).filter(Boolean);
}
/** true only when the pro EXPLICITLY lists `cityName` in his service_cities override —
 *  the predicate behind "featured in <city>" (Founder 2026-09-23: on a city where such a
 *  pro exists, he is THE expert and everyone else is dimmed "לא נותן שירות ב<city>"). */
export function serviceMode(p: MatchTarget): ServiceMode { return (p.serviceMode || p.service_mode || 'default') as ServiceMode; }
const ownCity = (p: MatchTarget) => p.proCity ?? p.city ?? null;
export function declaresCity(p: MatchTarget, cityName?: string | null): boolean {
  if (!cityName) return false;
  // 'city' mode = his own city is HIS (the Lior/Ashdod rule), same as an explicit service_cities entry
  if (serviceMode(p) === 'city' && ownCity(p) && normCity(ownCity(p)!) === normCity(cityName)) return true;
  return serviceCityList(p).some(c => normCity(c) === normCity(cityName));
}
/** true/false when this pro's own override decides whether he may match a visitor at
 *  (cityName, distKm); `null` = no override, the caller's shared radius rule decides. */
export function serviceAreaVerdict(p: MatchTarget, distKm: number, cityName?: string | null): boolean | null {
  const mode = serviceMode(p);
  if (mode === 'country') return true;
  if (mode === 'city') { const c = ownCity(p); if (cityName && c) return normCity(c) === normCity(cityName); return serviceCityList(p).length ? (cityName ? serviceCityList(p).some(x => normCity(x) === normCity(cityName)) : false) : false; }
  if (mode === 'radius') return distKm <= Number(p.serviceRadiusKm ?? DEFAULT_RADIUS_KM);
  const cities = serviceCityList(p);
  if (cities.length) {
    if (cityName) return cities.some(c => normCity(c) === normCity(cityName));
    // city name unknown → his own radius if he has one, else refuse (never leak out of area)
    if (p.serviceRadiusKm == null) return false;
  }
  if (p.serviceRadiusKm != null) return distKm <= Number(p.serviceRadiusKm);
  return null;
}
function passes(p: MatchTarget, distKm: number, km: number, cityName?: string | null): boolean {
  const v = serviceAreaVerdict(p, distKm, cityName);
  // 2026-09-28: a pro whose own city IS the requested city always matches it (the ranking puts him first — the filter must never drop him)
  return v == null ? (distKm <= km || isSameCity(ownCity(p), cityName)) : v;
}

// 2026-09-28 (bughunter): 22 active pros were stored at 31.4062525,35.0817938 — Google's geocode of "Israel" itself, returned when
// a city lookup fails — so they vanished from their own city page or ranked 160 km away. Every distance in this engine now goes
// through trustedCoords: a pro on that centroid, or > MAX_CITY_DRIFT_KM from the city he is listed in, is placed at his city.
const ISRAEL_CENTROID = { lat: 31.4062525, lng: 35.0817938 };
export const MAX_CITY_DRIFT_KM = 25;
export function trustedCoords(p: { lat: number | null; lng: number | null; city?: string | null; proCity?: string | null }): { lat: number; lng: number } | null {
  const own = findCity(p.proCity ?? p.city ?? null);
  if (p.lat == null || p.lng == null) return own ? { lat: own.lat, lng: own.lng } : null;
  const lat = Number(p.lat), lng = Number(p.lng);
  const onCentroid = Math.abs(lat - ISRAEL_CENTROID.lat) < 0.001 && Math.abs(lng - ISRAEL_CENTROID.lng) < 0.001;
  if (own && (onCentroid || haversineKm(own.lat, own.lng, lat, lng) > MAX_CITY_DRIFT_KM)) return { lat: own.lat, lng: own.lng };
  return onCentroid ? null : { lat, lng };
}
/** THE point a pro is measured from — his OWN city's verified point (lib/geo.ts, CBS); his stored point only when his city is
 *  unknown to the engine. Founder 2026-09-30 (Ramla → Modiin): distances were taken from each pro's business-address point to a
 *  2-decimal city centre, so a "Rehovot" pro whose address sits near Mazkeret Batya (12.2 km) ranked below a Modiin pro (11.7 km)
 *  although Rehovot is 7 km from Ramla and Modiin 14. Simulation 30/09 (30 cities × 12 categories = 360 quiz lists, 5 approaches):
 *  address points 38.6% of lists out of km order, distance buckets 65.8%, city-anchored 0% — city-anchored won. The visitor sees
 *  the pro's CITY, so the km we rank by (and print) are city-to-city km. */
export function proAnchor(p: { lat: number | null; lng: number | null; city?: string | null; proCity?: string | null }): { lat: number; lng: number } | null {
  const own = findCity(p.proCity ?? p.city ?? null);
  return own ? { lat: own.lat, lng: own.lng } : trustedCoords(p);
}
/** The visitor's city point: the verified locality when the name resolves, else the given coordinates (e.g. a Google pick). */
export function cityAnchor(name: string | null | undefined, lat: number, lng: number): { lat: number; lng: number } {
  const c = name ? findCity(name) : null; return c ? { lat: c.lat, lng: c.lng } : { lat, lng };
}
/** km from (lat,lng) to the pro's anchor; Infinity when unknown. */
export function proDistanceKm(p: { lat: number | null; lng: number | null; city?: string | null; proCity?: string | null }, lat: number, lng: number): number {
  const c = proAnchor(p); return c ? haversineKm(lat, lng, c.lat, c.lng) : Infinity;
}
/** The honest location note for a card (Founder 2026-09-30: "📍 X ק״מ מ<city>" when the pro is not in the chosen city).
 *  null when he IS in the city, when no city is chosen, or the distance is unknown. */
export function proCityDistanceNote(p: { lat: number | null; lng: number | null; city?: string | null; proCity?: string | null },
  cityName: string | null | undefined, coords: { lat: number; lng: number } | null): { km: number; city: string } | null {
  if (!cityName || !coords) return null;
  if (isSameCity(p.proCity ?? p.city ?? null, cityName)) return null;
  const a = cityAnchor(cityName, coords.lat, coords.lng);
  const d = proDistanceKm({ ...p, lat: p.lat != null ? Number(p.lat) : null, lng: p.lng != null ? Number(p.lng) : null }, a.lat, a.lng);
  return Number.isFinite(d) ? { km: roundKm(d), city: cityName } : null;
}
/** THE "we found N experts" line (Founder 2026-09-30: "מצאנו 11 מומחים ברמלה" was false — not all are FROM Ramla). Segments:
 *  strings + numbers; render numbers inside <bdi dir="ltr"> (RTL law), or join them for plain text (foundExpertsText). */
export function foundExpertsSegments(n: number, city: string | null | undefined, sameCityCount = 0): Array<string | number> {
  const c = (city || '').trim();
  const same = !c || sameCityCount <= 0 ? [] as Array<string | number>
    : sameCityCount === n ? [n === 1 ? ' (מהעיר עצמה)' : ' (כולם מהעיר עצמה)']
    : sameCityCount === 1 ? [' (אחד מהעיר עצמה)'] : [' (', sameCityCount, ' מהעיר עצמה)'];
  if (n === 1) return [`מצאנו מומחה אחד${c ? ` שנותן שירות ב${c}` : ''}`, ...same];
  return ['מצאנו ', n, ` מומחים${c ? ` שנותנים שירות ב${c}` : ''}`, ...same];
}
export function foundExpertsText(n: number, city: string | null | undefined, sameCityCount = 0): string {
  return foundExpertsSegments(n, city, sameCityCount).join('');
}

export type MatchResult<T> = {
  matched: T[];
  radiusKm: number | null; // null = fell all the way back to "everyone" (no real geo match)
  expanded: boolean;       // true if it took more than the DEFAULT_RADIUS_KM step to find enough
};

/**
 * Finds pros near a city, expanding the search radius step by step until at least
 * `minMatches` are found (or the ladder is exhausted). Never returns an empty list when
 * `all.length > 0` — the final fallback is the full unfiltered list, distance-sorted.
 */
export function matchProsToCity<T extends MatchTarget>(
  all: T[], cityLat: number, cityLng: number, minMatches = MIN_MATCHES, cityName?: string | null
): MatchResult<T> {
  // Founder 2026-09-23 (ליאור/אשדוד): a city that at least one pro EXPLICITLY declares in
  // service_cities belongs to those pros ONLY — the list shows the rest dimmed ("לא נותן
  // שירות ב<city>") and, by the SAME predicate here, the lead/recipient strip (QuickConsultBar)
  // and the server-side WhatsApp routing (lib/waLinks.ts) never send that city's inquiry
  // to anyone else. One predicate, every consumer.
  const declared = all.filter(p => declaresCity(p, cityName));
  if (declared.length) return { matched: declared, radiusKm: DEFAULT_RADIUS_KM, expanded: false };
  ({ lat: cityLat, lng: cityLng } = cityAnchor(cityName, cityLat, cityLng));
  for (const km of RADIUS_LADDER_KM) {
    const within = all.filter(p => passes(p, proDistanceKm(p, cityLat, cityLng), km, cityName));
    // a list smaller than minMatches (a service page's few pros, 21/10/26) can never reach it — "all of them within km" is the full answer
    if (within.length >= Math.min(minMatches, all.length) && within.length) return { matched: within, radiusKm: km, expanded: km > DEFAULT_RADIUS_KM };
  }
  // Ladder exhausted — return everyone, nearest first, rather than an empty result —
  // minus pros with a service-area override (they never leave their declared area).
  const pool = all.filter(p => serviceAreaVerdict(p, proDistanceKm(p, cityLat, cityLng), cityName) !== false);
  if (!pool.length) return { matched: [], radiusKm: null, expanded: true };
  const sorted = [...pool].sort((a, b) => {
    const da = proDistanceKm(a, cityLat, cityLng);
    const db = proDistanceKm(b, cityLat, cityLng);
    return da - db;
  });
  return { matched: sorted, radiusKm: null, expanded: true };
}

/** Just the count, at the fixed default radius — for display/index-eligibility checks
 * (lib/categoryData.ts's prosInRadius) that intentionally do NOT want ladder expansion. */
export function prosWithinDefaultRadius(all: MatchTarget[], cityLat: number, cityLng: number, cityName?: string | null): number {
  return all.filter(p => passes(p, proDistanceKm(p, cityLat, cityLng), DEFAULT_RADIUS_KM, cityName)).length;
}

/** THE single "does this pro serve this city" rule (bughunter BLOCKER 2026-09-28: the list widened to 40km and showed a pro
 *  whose own page then refused the same city at a fixed 25km → an endless list ↔ pro-page loop). Explicit service area wins;
 *  otherwise the same ladder the listing uses, up to its widest rung. */
export function proServesCity(p: MatchTarget, distKm: number, cityName?: string | null): boolean {
  const v = serviceAreaVerdict(p, distKm, cityName);
  return v == null ? distKm <= RADIUS_LADDER_KM[RADIUS_LADDER_KM.length - 1] : v;
}

/** Same city? — by the canonical dataset city (findCity resolves 'תל אביב-יפו'→'תל אביב', 'קריית'/'קרית', slug dashes),
 *  falling back to a plain normalized string compare for towns outside the dataset. */
export function isSameCity(a?: string | null, b?: string | null): boolean {
  if (!a || !b) return false;
  const spell = (s: string) => normCity(s.replace(/קריית/g, 'קרית').replace(/תקוה/g, 'תקווה'));
  if (spell(a) === spell(b) || sameLocality(a, b)) return true;
  const ca = findCity(a), cb = findCity(b);
  return !!ca && !!cb && ca.name === cb.name;
}
type RankTarget = RankScoreInput & { lat: number | null; lng: number | null; city?: string | null; proCity?: string | null; rating10?: number | null; rating?: number | string | null;
  dealsClosed?: number | null; deals_closed?: number | string | null; inquiriesReceived?: number | null; inquiries_received?: number | string | null };
/** + Founder 2026-09-30: between equal distances, closed deals then inquiries received beat rating (lib/proTrack.ts).
 *  THE ranking for a city (Founder 2026-09-28, /עורך-דין-תעבורה/ראשון-לציון showed an Eilat lawyer above the Rishon one):
 *  (1) experts whose own city IS the visitor's city, (2) then nearest first by distance from the city, (3) rating only as the
 *  tiebreak. Applies to EVERY category — nationwide ones too (they are never filtered by city, but they are ORDERED by it).
 *  Used by the category list, QuickConsultBar, QuickConsultWidget and the WhatsApp routing (lib/waLinks) — one rule. */
/** Founder 04/10/26 (SITE-WIDE, overrides the 09-28 same-city law ONLY for location_independent=1 categories — all lawyers + mortgage):
 *  the order is ONE combined score of high rating AND many reviews, the Bayesian average
 *      score = (v/(v+m))·R + (m/(v+m))·C     (R = the DISPLAYED rating 1-5, v = the DISPLAYED count, C = the mean rating of the list, m = BAYES_M)
 *  so 9.9 with 7 reviews never beats 9.8 with 170. City / distance never affect it. Ties: more reviews → rank score → rating. */
export const BAYES_M = 10;
// DECIDED 04/10 (bughunter + coordinator): R and v are the DISPLAYED numbers — the rating printed on the card (raw Google 1-5) and
// the count printed on the card = the pager total. A surface passes its own accessors when its objects differ (x3d: r / rt).
const num0 = (v: any) => (v == null || v === '' ? null : Number(v));
export const displayedRating = (p: any): number => { const d = num0(p.dr) ?? num0(p.rating5) ?? num0(p.rating10) ?? num0(p.r) ?? num0(p.rating); return d == null ? 0 : d; }; // stored = shown = raw 1-5 since 04/10/26 — no conversion
export const displayedCount = (p: any): number => num0(p.dv) ?? num0(p.count5) ?? num0(p.reviewCount) ?? num0(p.rt) ?? num0(p.review_count) ?? num0(p.c) ?? 0;
export function bayesianScore(rating: number, reviews: number, mean: number, m = BAYES_M): number {
  const v = Math.max(0, reviews || 0); return v + m > 0 ? (v / (v + m)) * (rating || 0) + (m / (v + m)) * mean : mean;
}
export type ScoreAccess = { rating?: (p: any) => number; reviews?: (p: any) => number };
/** The combined score of every pro of a list (C = the list's mean displayed rating) — the gate checks displayed orders with it. */
export function combinedScores<T>(all: T[], acc: ScoreAccess = {}): { p: T; b: number; v: number; R: number }[] {
  const R_ = acc.rating || displayedRating, V_ = acc.reviews || displayedCount;
  const rated = all.filter(p => V_(p) > 0 && R_(p) > 0);
  const C = rated.length ? rated.reduce((s, p) => s + R_(p), 0) / rated.length : 0;
  return all.map(p => ({ p, R: R_(p), v: V_(p), b: bayesianScore(R_(p), V_(p), C) }));
}
export function rankByCombinedScore<T>(all: T[], acc: ScoreAccess = {}): T[] {
  return combinedScores(all, acc).map(x => ({ ...x, s: rankScore(x.p as any) }))
    .sort((a, b) => (b.b - a.b) || (b.v - a.v) || (b.s - a.s) || (b.R - a.R)).map(x => x.p);
}
/** 19/10/26 Founder: specialists of a SUBCATEGORY first. specialist_score = MAX he_pro_subcategory_evidence for the page's slug (lib/categoryData);
 *  a STABLE sort on top of any ranking, so a page with no evidence rows (all 0) keeps its order exactly. */
export const specialistOf = (p: any): number => Number(p?.specialist_score ?? p?.specialistScore ?? 0) || 0;
export function specialistsFirst<T>(list: T[]): T[] { return list.some(p => specialistOf(p) > 0) ? list.map((p, i) => ({ p, i })).sort((a, b) => (specialistOf(b.p) - specialistOf(a.p)) || (a.i - b.i)).map(x => x.p) : list; }
export function rankProsForCity<T extends RankTarget>(all: T[], city: { name?: string | null; lat: number; lng: number } | null, opts: { locationIndependent?: boolean } & ScoreAccess = {}): T[] { return specialistsFirst(rankProsForCityBase(all, city, opts)); }
function rankProsForCityBase<T extends RankTarget>(all: T[], city: { name?: string | null; lat: number; lng: number } | null, opts: { locationIndependent?: boolean } & ScoreAccess = {}): T[] {
  if (opts.locationIndependent) return rankByCombinedScore(all, opts); // nationwide categories: rating × reviews only (Founder 04/10, site-wide)
  // Founder 01/10/26: rank score (inquiries + promotion, lib/proRankScore) — no city → score first, incoming order as tie-break
  if (!city) return sortByRankScore(all);
  city = { ...city, ...cityAnchor(city.name, city.lat, city.lng) };
  const own = (p: T) => p.proCity ?? p.city ?? null;
  const rating = (p: T) => Number((p as any).ratingRaw ?? (p as any).rating_raw ?? p.rating10 ?? p.rating ?? 0) || 0; // stored value (order input), never the shown raw 1-5
  // track record (lib/proTrack.ts): closed deals, then inquiries — only between EQUAL distances (same-city peers share one point)
  const deals = (p: T) => Number(p.dealsClosed ?? p.deals_closed ?? 0) || 0;
  const inqs = (p: T) => Number(p.inquiriesReceived ?? p.inquiries_received ?? 0) || 0;
  const dist = (p: T) => proDistanceKm({ ...p, lat: p.lat != null ? Number(p.lat) : null, lng: p.lng != null ? Number(p.lng) : null }, city.lat, city.lng);
  return all
    .map(p => ({ p, same: isSameCity(own(p), city.name) ? 1 : 0, d: dist(p) }))
    .map(x => ({ ...x, s: rankScore(x.p) }))
    // same city (28/09 law) → RANK SCORE (01/10: inquiries + promotion) → distance → deals → inquiries → rating
    .sort((a, b) => (b.same - a.same) || (b.s - a.s) || (Math.abs(a.d - b.d) < 0.05 ? 0 : a.d - b.d) || (deals(b.p) - deals(a.p)) || (inqs(b.p) - inqs(a.p)) || (rating(b.p) - rating(a.p)) || (displayedCount(b.p) - displayedCount(a.p)))
    .map(x => x.p);
}

/** THE list a /<category>/<city> page shows, in its exact order (bughunter 2026-09-30: the homepage "טכנאי מזגנים מומלצים
 *  בתל אביב" strip ranked on its own — 6 cards, 1 in Tel Aviv, Haifa above Rehovot). One pipeline for the category list
 *  (components/CategoryExpertsList) and the homepage strip (lib/homeExperts), so the home cards for a sample ARE the top cards
 *  of the real city page:
 *  (1) campaign mode — pros who explicitly declare the city first, then everyone else (the page dims them), each ranked;
 *  (2) otherwise, non-nationwide — the radius ladder (matchProsToCity), ranked; nationwide — everyone, ranked;
 *  ranking = rankProsForCity (same city → distance → rating); (3) pros away until a later date go last. */
export function cityPageOrder<T extends RankTarget & MatchTarget & { id: number; avail?: ProAvailInput }>(
  experts: T[], cityName: string | null | undefined, coords: { lat: number; lng: number } | null,
  opts: { nationwide?: boolean; featuredId?: number | null; now?: Date } = {},
): { list: T[]; featuredIds: Set<number>; campaignMode: boolean; matchInfo: MatchResult<T> | null } {
  const { nationwide = false, featuredId = null } = opts;
  const ranked = rankProsForCity(experts, coords ? { name: cityName, ...coords } : null, { locationIndependent: nationwide });
  const matchInfo = coords ? matchProsToCity(experts, coords.lat, coords.lng, MIN_MATCHES, cityName) : null;
  const declared = (!nationwide && cityName) ? experts.filter(e => declaresCity(e, cityName)) : [];
  const one = featuredId ? declared.find(e => e.id === featuredId) : undefined;
  const featured = declared.length ? (one ? [one] : declared) : [];
  const featuredIds = new Set(featured.map(e => e.id));
  const campaignMode = featured.length > 0;
  let shown: T[];
  if (campaignMode) shown = [...featured, ...ranked.filter(e => !featuredIds.has(e.id))];
  else if (!nationwide && cityName && matchInfo) { const ids = new Set(matchInfo.matched.map(e => e.id)); shown = ranked.filter(e => ids.has(e.id)); }
  else shown = ranked;
  const away = (e: T) => isAwayUntilLater(e.avail || {}, opts.now);
  return { list: [...shown.filter(e => !away(e)), ...shown.filter(away)], featuredIds, campaignMode, matchInfo };
}
